import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CheckCircle2, Loader2, Lock, Paperclip, Trophy } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { TrainingVideoPlayer } from '@/components/agent-handbook/TrainingVideoPlayer';
import { ChannelBadges } from '@/components/agent-handbook/ChannelBadges';
import { trainingMediaUrl } from '@/components/agent-handbook/trainingMedia';

type TrainingAttachment = {
    id?: number;
    name: string;
    url?: string | null;
    objectKey?: string | null;
};

type TrainingDetail = {
    id: number;
    title: string;
    category: string;
    videoTitle?: string | null;
    videoUrl?: string | null;
    videoObjectKey?: string | null;
    trainingMode?: 'scheduled' | 'stage' | string | null;
    scheduledDate?: string | null;
    endDate?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    startsAt?: string | null;
    endsAt?: string | null;
    availableFrom?: string | null;
    procrastinationDays?: number | null;
    createdAt?: string | null;
    isAvailable?: boolean;
    lockReason?: string | null;
    videos?: {
        title?: string | null;
        url?: string | null;
        objectKey?: string | null;
        originalName?: string | null;
        description?: string | null;
        required?: boolean | number;
    }[];
    checklistText: string;
    attachments?: TrainingAttachment[];
    trainingRanks?: { id: number; name: string; level: number; medal?: string | null }[];
    minTrainingRank?: { id: number; name: string; level: number; medal?: string | null } | null;
    hasQuiz?: boolean;
    quizEnabled?: number | boolean;
    quizQuestions?: {
        id: string;
        prompt: string;
        answerType: 'objective' | 'text';
        correctAnswer?: string;
        options?: string[];
    }[];
    notifyInApp?: number | boolean;
    notifyEmail?: number | boolean;
    notifyWebPush?: number | boolean;
    progress?: {
        maxWatchedSeconds?: number;
        lastPositionSeconds?: number;
        durationSeconds?: number;
        watchedAt?: string | null;
        completedAt?: string | null;
    } | null;
};

type ScheduledTrainingLike = Pick<TrainingDetail, 'scheduledDate' | 'endDate' | 'startTime' | 'endTime' | 'startsAt' | 'endsAt' | 'isAvailable'>;

const trainingStartDate = (item: ScheduledTrainingLike) => {
    if (item.startsAt) {
        const parsed = new Date(item.startsAt);
        if (isValid(parsed)) return parsed;
    }
    if (!item.scheduledDate || !item.startTime) return null;
    const [year, month, day] = item.scheduledDate.slice(0, 10).split('-').map(Number);
    const [hour = 0, minute = 0, second = 0] = item.startTime.split(':').map(Number);
    const parsed = new Date(year, month - 1, day, hour, minute, second);
    return isValid(parsed) ? parsed : null;
};

const isTrainingTimeAvailable = (item: ScheduledTrainingLike, now = new Date()) => {
    if (item.isAvailable === false) return false;
    const startsAt = trainingStartDate(item);
    if (startsAt) return now >= startsAt;
    return item.isAvailable !== false;
};

const formatTrainingStart = (item: ScheduledTrainingLike) => {
    const startsAt = trainingStartDate(item);
    return startsAt ? format(startsAt, 'MMM d, yyyy h:mm a') : '—';
};

const formatTrainingWindow = (item: ScheduledTrainingLike) => {
    const startsAt = trainingStartDate(item);
    if (!startsAt) return '—';
    const startLabel = format(startsAt, 'MMM d, yyyy h:mm a');
    if (item.endsAt) {
        const parsedEnd = new Date(item.endsAt);
        if (isValid(parsedEnd)) return `${startLabel} - ${format(parsedEnd, 'MMM d, yyyy h:mm a')}`;
    }
    const endDate = item.endDate?.slice(0, 10);
    const end = item.endTime?.slice(0, 5);
    return endDate && end ? `${startLabel} - ${endDate} ${end}` : `${startLabel} onwards`;
};

// Self-paced stage: just show how long it's been due (or how long it took) — no calendar.
const StageDueBanner = ({ item }: { item: TrainingDetail }) => {
    if (item.trainingMode !== 'stage') return null;
    const completed = Boolean(item.progress?.completedAt);
    const available = item.isAvailable !== false;
    if (!completed && !available) return null; // locked — nothing due yet
    const days = Math.max(0, Number(item.procrastinationDays || 0));
    const tone = completed
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : days > 0
            ? 'border-amber-200 bg-amber-50 text-amber-700'
            : 'border-border bg-muted/40 text-foreground';
    return (
        <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium ${tone}`}>
            <CalendarClock className="h-4 w-4 shrink-0" />
            {completed
                ? `Completed in ${days} day${days === 1 ? '' : 's'}.`
                : days > 0
                    ? `${days} day${days === 1 ? '' : 's'} due`
                    : 'Due today'}
        </div>
    );
};

const TrainingDetailPage = () => {
    const { trainingId } = useParams<{ trainingId: string }>();
    const navigate = useNavigate();
    const [item, setItem] = useState<TrainingDetail | null>(null);
    const [allItems, setAllItems] = useState<TrainingDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [videoIndex, setVideoIndex] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
    const [rankCelebration, setRankCelebration] = useState<{ from?: { name: string }; to?: { name: string }; pointsEarned?: number; totalPoints?: number; rankChanged?: boolean } | null>(null);
    const [nowTick, setNowTick] = useState(() => new Date());

    useEffect(() => {
        const id = trainingId;
        if (!id) {
            setError('Missing training id.');
            setLoading(false);
            return;
        }

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get(`/agent-handbook/training/${id}`);
                if (res.data?.status && (res.data.item || res.data.training)) {
                    const row = (res.data.item || res.data.training) as TrainingDetail;
                    setItem(row);
                    setLoading(false);
                    return;
                }
            } catch {
                /* fallback */
            }

            try {
                const dash = await api.get('/agent-handbook');
                if (!dash.data?.status) throw new Error(dash.data?.error || 'Failed to load training.');
                const items = (dash.data.trainingItems || []) as TrainingDetail[];
                setAllItems(items);
                const row = items.find(t => String(t.id) === String(id));
                if (!row) throw new Error('Training module not found.');
                setItem(row);
            } catch (err: any) {
                setError(Helper.errorHandler(err));
                toast.error(Helper.errorHandler(err));
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, [trainingId]);

    useEffect(() => {
        const timer = window.setInterval(() => setNowTick(new Date()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    const updateProgress = (progress: TrainingDetail['progress']) => {
        setItem(current => (current ? { ...current, progress } : current));
    };

    const recordTrainingProgress = async (payload: Record<string, any>, silent = true) => {
        if (!item) return false;
        try {
            const response = await api.post(`/agent-handbook/training/${item.id}/progress`, payload, {
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.data.status) throw new Error(response.data.error);
            updateProgress(response.data.progress);
            if (response.data.rankAdvanced?.rankChanged) setRankCelebration(response.data.rankAdvanced);
            if (!silent) toast.success(payload.action === 'completed' ? 'Lesson completed!' : 'Progress saved');
            return { ok: true, rankAdvanced: response.data.rankAdvanced };
        } catch (err: any) {
            if (!silent) toast.error(Helper.errorHandler(err));
            return { ok: false, rankAdvanced: null };
        }
    };

    const markComplete = async () => {
        if (!item) return;
        setSaving(true);
        const answers = (item.quizQuestions || []).map(question => ({
            questionId: question.id,
            prompt: question.prompt,
            answer: quizAnswers[question.id] || '',
        }));
        const result = await recordTrainingProgress({ action: 'completed', quizAnswers: answers }, false);
        setSaving(false);
        if (result.ok && !result.rankAdvanced) navigate('/agent-handbook?tab=training');
    };

    const isStage = item?.trainingMode === 'stage';
    const timeUnlocked = item ? isTrainingTimeAvailable(item, nowTick) : false;
    // Stage = gated by the previous stage (server-resolved). Scheduled = gated only by its date/time.
    const canWatch = isStage ? (item?.isAvailable !== false) : timeUnlocked;
    const completed = Boolean(item?.progress?.completedAt);
    const locked = !canWatch;
    const lockedMessage = isStage
        ? (item?.lockReason || 'Complete the previous stage before starting this one.')
        : (item ? `Training starts on ${formatTrainingStart(item)}.` : 'This training has not started yet.');
    const videos = item ? [
        ...(item.videoObjectKey || item.videoUrl ? [{ title: item.videoTitle, objectKey: item.videoObjectKey, url: item.videoUrl, required: true }] : []),
        ...(item.videos || []),
    ].filter((video, index, rows) => (video.objectKey || video.url) && rows.findIndex(row => (row.objectKey || row.url) === (video.objectKey || video.url)) === index)
        .map((video, index) => ({ ...video, required: video.required === undefined ? index === 0 : Boolean(video.required) })) : [];
    const activeVideo = videos[videoIndex] || videos[0];
    const hasVideo = videos.length > 0;
    const hasRequiredVideo = videos.some(v => v.required);
    // The "watched" gate is only meaningful if at least one video is compulsory.
    // If everything is optional (or there's no video at all), we unlock the quiz
    // and Complete button right away — there's nothing the agent must sit through.
    const watched = hasRequiredVideo ? Boolean(item?.progress?.watchedAt) : Boolean(item);
    const requiresQuiz = Boolean(item?.quizEnabled || item?.hasQuiz) && Boolean(item?.quizQuestions?.length);
    const quizReady = !requiresQuiz || (item?.quizQuestions || []).every(question => (quizAnswers[question.id] || '').trim());

    if (loading) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error || !item) {
        return (
            <div className="mx-auto max-w-lg space-y-4 p-6">
                <Button variant="ghost" size="sm" asChild>
                    <Link to="/agent-handbook?tab=training" className="inline-flex items-center gap-2">
                        <ArrowLeft className="h-4 w-4" />
                        Back to training
                    </Link>
                </Button>
                <p className="text-sm text-destructive">{error || 'Not found.'}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12">
            {rankCelebration && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl border bg-background p-8 text-center shadow-2xl">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                            <Trophy className="h-8 w-8" />
                        </div>
                        <h2 className="text-2xl font-semibold">Congratulations</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            You advanced from {rankCelebration.from?.name || 'your previous rank'} to {rankCelebration.to?.name || 'the next rank'}.
                        </p>
                        {Number(rankCelebration.pointsEarned || 0) > 0 && (
                            <p className="mt-3 text-sm font-medium text-emerald-600">
                                +{Number(rankCelebration.pointsEarned).toLocaleString()} points · {Number(rankCelebration.totalPoints || 0).toLocaleString()} total
                            </p>
                        )}
                        <Button className="mt-6 w-full" onClick={() => navigate('/agent-handbook?tab=training')}>
                            Continue
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 border-b pb-4">
                <Button variant="ghost" size="icon" asChild className="h-9 w-9">
                    <Link to="/agent-handbook?tab=training" className="inline-flex items-center gap-2">
                        <ArrowLeft className="h-4 w-4" />
                    </Link>
                </Button>
                <div className="min-w-0 flex-1">
                    <h1 className="truncate text-2xl font-semibold tracking-tight text-foreground">{item.title}</h1>
                    {item.videoTitle && <p className="mt-1 text-sm text-muted-foreground">{item.videoTitle}</p>}
                </div>
                <Badge variant="outline">{item.category}</Badge>
                <Badge variant="outline" className="inline-flex items-center gap-1.5">
                    <CalendarClock className="h-3.5 w-3.5" />
                    {formatTrainingWindow(item)}
                </Badge>
                {(item.trainingRanks?.length || item.minTrainingRank) && (
                    <Badge variant="secondary">
                        {item.trainingRanks?.length ? item.trainingRanks.map(rank => rank.name).join(', ') : item.minTrainingRank?.name}
                    </Badge>
                )}
            </div>

            <StageDueBanner item={item} />

            {Boolean(item.checklistText) && <div className="rounded-md bg-muted/50 p-4 text-sm text-foreground/90">{item.checklistText}</div>}

            {Boolean(item.attachments?.length) && (
                <div className="space-y-2">
                    <h2 className="text-sm font-medium text-foreground">Attachments</h2>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {item.attachments!.map((a, i) => {
                            const href = trainingMediaUrl(a.objectKey || a.url);
                            const ext = (a.name || '').split('.').pop()?.toLowerCase() || 'file';
                            return (
                                <a
                                    key={a.id ?? i}
                                    href={href || '#'}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={a.name || undefined}
                                    className="group flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-muted/50"
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
                                        {ext.slice(0, 4)}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-medium text-foreground">{a.name}</span>
                                        <span className="block text-xs text-muted-foreground">Click to download</span>
                                    </span>
                                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
                                </a>
                            );
                        })}
                    </div>
                </div>
            )}

            {locked ? (
                <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-5 text-sm text-muted-foreground">
                    <Lock className="h-5 w-5" />
                    {lockedMessage}
                </div>
            ) : activeVideo ? (
                <div className="space-y-3">
                    {videos.length > 1 && (
                        <div className="flex flex-wrap gap-2">
                            {videos.map((video, index) => (
                                <Button
                                    key={`${video.objectKey || video.url}-${index}`}
                                    type="button"
                                    size="sm"
                                    variant={index === videoIndex ? 'default' : 'outline'}
                                    onClick={() => setVideoIndex(index)}
                                >
                                    {video.title || video.originalName || `Video ${index + 1}`}
                                    <span className="ml-1 text-[10px] opacity-70">{video.required ? 'Required' : 'Optional'}</span>
                                </Button>
                            ))}
                        </div>
                    )}
                    <TrainingVideoPlayer
                        item={{
                            ...item,
                            videoTitle: activeVideo.title || activeVideo.originalName || item.videoTitle,
                            videoUrl: activeVideo.url || activeVideo.objectKey,
                            videoObjectKey: activeVideo.objectKey || activeVideo.url,
                        }}
                        onProgress={async (payload, silent) => activeVideo.required ? (await recordTrainingProgress(payload, silent)).ok : true}
                    />
                    {activeVideo.description && <p className="text-sm text-muted-foreground">{activeVideo.description}</p>}
                </div>
            ) : requiresQuiz ? null : (
                <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">No video or quiz attached to this module.</div>
            )}

            <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <ChannelBadges item={item} />
                {!requiresQuiz && (
                    <Button type="button" disabled={locked || !watched || completed || saving} onClick={() => void markComplete()}>
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : completed ? (
                            <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Completed</span>
                        ) : locked ? 'Locked' : watched ? 'Complete' : 'Complete video'}
                    </Button>
                )}
            </div>

            {requiresQuiz && (
                <div className="space-y-4 rounded-md border p-4">
                    <div className="space-y-1">
                        <h2 className="text-sm font-medium text-foreground">Quiz</h2>
                        <p className="text-xs text-muted-foreground">
                            {completed
                                ? 'You have completed this quiz.'
                                : watched
                                    ? 'Answer every question below, then submit to complete this training.'
                                    : 'Finish the required video(s) above first. The quiz will unlock here once you are done.'}
                        </p>
                    </div>

                    {watched && !completed && (
                        <>
                            <div className="space-y-3">
                                {item.quizQuestions!.map((question, index) => (
                                    <div key={question.id} className="space-y-1.5">
                                        <label className="text-sm text-foreground">{index + 1}. {question.prompt}</label>
                                        {question.answerType === 'text' ? (
                                            <Textarea value={quizAnswers[question.id] || ''} onChange={event => setQuizAnswers(current => ({ ...current, [question.id]: event.target.value }))} />
                                        ) : (
                                            <div className="grid gap-2 sm:grid-cols-2">
                                                {(question.options?.length ? question.options : ['A', 'B', 'C', 'D']).map((option, optionIndex) => (
                                                    <button
                                                        key={`${question.id}-${optionIndex}`}
                                                        type="button"
                                                        className={`rounded-md border px-3 py-2 text-left text-sm ${quizAnswers[question.id] === option ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted/50'}`}
                                                        onClick={() => setQuizAnswers(current => ({ ...current, [question.id]: option }))}
                                                    >
                                                        <span className="mr-2 font-semibold">{String.fromCharCode(65 + optionIndex)}.</span>
                                                        {option}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground">Objective answers are checked immediately. Text answers are sent to admin review before completion.</p>
                            <div className="flex justify-end border-t pt-3">
                                <Button type="button" disabled={completed || saving || !quizReady} onClick={() => void markComplete()}>
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit quiz'}
                                </Button>
                            </div>
                        </>
                    )}

                    {completed && (
                        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            Completed
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TrainingDetailPage;
